/**
 * ALTIORA · Relationship Timeline
 * ---------------------------------------------------------------
 * Chronologie relationnelle universelle. Peut être montée sur toute
 * page d'entité (Prospect, Client, Partenaire, Organisation) en
 * précisant `subjectType` et `subjectId`.
 *
 * <RelationshipTimeline subjectType="client" subjectId="cl_abc" />
 */
import { useEffect, useState } from "react";
import {
  MessageSquare, Sparkles, GitBranch, RefreshCw, Star, TrendingUp,
  Shield, Calendar, Clock, FileText, ArrowRight, ListChecks, Filter,
  Loader2,
} from "lucide-react";
import { api } from "@/lib/api";

const EVENT_META = {
  interaction:      { label: "Interaction",           icon: MessageSquare, color: "#6b7c93" },
  recommendation:   { label: "Recommandation",        icon: Sparkles,       color: "#c9a34e" },
  decision:         { label: "Décision",              icon: GitBranch,      color: "#0a2540" },
  situation_change: { label: "Changement de situation",icon: RefreshCw,     color: "#8B4E00" },
  key_event:        { label: "Événement important",   icon: Star,           color: "#c9a34e" },
  opportunity:      { label: "Opportunité",           icon: TrendingUp,     color: "#0a7f42" },
  risk:             { label: "Risque",                icon: Shield,         color: "#c95050" },
  appointment:      { label: "Rendez-vous",           icon: Calendar,       color: "#0a2540" },
  deadline:         { label: "Échéance",              icon: Clock,          color: "#c9a34e" },
  contract:         { label: "Contrat",               icon: FileText,       color: "#0a7f42" },
  followup:         { label: "Suivi",                 icon: ArrowRight,     color: "#6b7c93" },
};

const ALL_TYPES = Object.keys(EVENT_META);

function fmt(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("fr-CA", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function RelationshipTimeline({ subjectType, subjectId, className = "" }) {
  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTypes, setSelectedTypes] = useState([]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const qs = selectedTypes.length ? `?event_types=${selectedTypes.join(",")}` : "";
      const [ev, st] = await Promise.all([
        api.get(`/timeline/${subjectType}/${subjectId}${qs}`),
        api.get(`/timeline/${subjectType}/${subjectId}/stats`),
      ]);
      setEvents(ev.data.events || []);
      setStats(st.data);
    } catch {
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (subjectId) fetchData(); }, [subjectType, subjectId, selectedTypes.join(",")]);

  return (
    <div className={className} data-testid={`rt-root-${subjectType}-${subjectId}`}>
      <div className="flex items-center justify-between mb-3 gap-3">
        <div className="flex items-center gap-2">
          <ListChecks size={15} style={{ color: "#c9a34e" }} />
          <h3 className="font-serif text-sm" style={{ color: "#0a2540" }}>
            Chronologie relationnelle{" "}
            <span className="text-xs" style={{ color: "#6b7c93" }}>
              ({stats?.total ?? 0})
            </span>
          </h3>
        </div>
        <FilterMenu
          types={ALL_TYPES}
          stats={stats?.by_type || {}}
          selected={selectedTypes}
          onToggle={(t) => setSelectedTypes((s) =>
            s.includes(t) ? s.filter((x) => x !== t) : [...s, t]
          )}
          onReset={() => setSelectedTypes([])}
        />
      </div>

      {loading ? (
        <div className="text-center py-6" data-testid="rt-loading">
          <Loader2 className="animate-spin inline" size={16} />
        </div>
      ) : events.length === 0 ? (
        <div className="text-xs text-center py-6"
              style={{ color: "#6b7c93" }}
              data-testid="rt-empty">
          Aucun événement enregistré pour cette relation.
        </div>
      ) : (
        <ul className="space-y-0" data-testid="rt-events">
          {events.map((ev, idx) => {
            const meta = EVENT_META[ev.event_type] || { label: ev.event_type, icon: MessageSquare, color: "#6b7c93" };
            const Icon = meta.icon;
            return (
              <li
                key={ev.id}
                className="relative pl-8 pb-4"
                data-testid={`rt-event-${ev.id}`}
                data-event-type={ev.event_type}
              >
                {idx !== events.length - 1 && (
                  <span
                    className="absolute left-3 top-6 w-px"
                    style={{ background: "#e5e0d0", height: "calc(100% - 12px)" }}
                  />
                )}
                <span
                  className="absolute left-0 top-0 w-6 h-6 rounded-full flex items-center justify-center"
                  style={{ background: `${meta.color}22`, border: `1px solid ${meta.color}88` }}
                >
                  <Icon size={11} style={{ color: meta.color }} />
                </span>
                <div className="flex items-baseline justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] font-mono uppercase tracking-widest"
                         style={{ color: meta.color }}>
                      {meta.label}
                      {ev.source_module && (
                        <span className="ml-2 text-[9px]" style={{ color: "#6b7c93" }}>
                          via {ev.source_module}
                        </span>
                      )}
                    </div>
                    <div className="text-sm font-medium" style={{ color: "#0a2540" }}>
                      {ev.title}
                    </div>
                    {ev.description && (
                      <div className="text-xs mt-0.5" style={{ color: "#1e2a3a" }}>
                        {ev.description}
                      </div>
                    )}
                    {ev.tags?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {ev.tags.map((t) => (
                          <span key={t} className="text-[9px] px-1.5 py-0.5 rounded"
                                style={{ background: "#f3ecd7", color: "#0a2540" }}>
                            {t}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <time className="text-[10px] font-mono whitespace-nowrap"
                        style={{ color: "#6b7c93" }}>
                    {fmt(ev.occurred_at)}
                  </time>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function FilterMenu({ types, stats, selected, onToggle, onReset }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded"
        style={{ background: "#faf6ea", color: "#0a2540",
                  border: "1px solid #e5d9b8" }}
        data-testid="rt-filter-btn"
      >
        <Filter size={11} /> Filtrer{selected.length > 0 ? ` (${selected.length})` : ""}
      </button>
      {open && (
        <div className="absolute right-0 mt-1 w-64 bg-white shadow-lg rounded p-2 z-20"
             style={{ border: "1px solid #e5e0d0" }}
             data-testid="rt-filter-menu">
          {types.map((t) => {
            const meta = EVENT_META[t];
            const on = selected.includes(t);
            return (
              <label key={t} className="flex items-center gap-2 text-xs py-1 cursor-pointer">
                <input type="checkbox" checked={on} onChange={() => onToggle(t)}
                        data-testid={`rt-filter-${t}`} />
                <span style={{ color: meta.color }}>●</span>
                <span className="flex-1" style={{ color: "#0a2540" }}>{meta.label}</span>
                <span className="text-[10px]" style={{ color: "#6b7c93" }}>
                  {stats[t] || 0}
                </span>
              </label>
            );
          })}
          <div className="mt-2 pt-2 border-t flex justify-between"
               style={{ borderColor: "#e5e0d0" }}>
            <button onClick={onReset}
                     className="text-[10px] hover:underline"
                     style={{ color: "#6b7c93" }}
                     data-testid="rt-filter-reset">
              Effacer
            </button>
            <button onClick={() => setOpen(false)}
                     className="text-[10px] hover:underline"
                     style={{ color: "#0a2540" }}>
              Fermer
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
